<template>
  <b-container>
    <TheTitle>營銷人員</TheTitle>
    <OverviewList :overviews="overviews"
                  class-name="col-6 col-md-3"
                  v-stream:select="onOverviewSelect$"
                  :loading="loading"
    ></OverviewList>
    <TheListingTitle
      title="營銷人員列表"
      :selectedKeywordAndFilters="selectedKeywordAndFilters"
      @reset-click="initialize"
    ></TheListingTitle>
    <section class="table-content">
      <BaseFilterToolbar @reset="initialize" :loading="loading">
        <div class="d-flex">
          <label>
            <b-form-select class="filter keyword-select" v-model="filter.keywordType">
              <b-form-select-option
                  v-for="option in filteredKeywordSelectOptions"
                  :value="option.value"
                  :key="option.text">
                  {{ option.text }}
                </b-form-select-option>
            </b-form-select>
          </label>
          <label id="keyword-filter">
            <input
              v-model="keyword"
              type="text"
              class="form-control"
              placeholder="關鍵字..."
              aria-label="關鍵字..."
              aria-describedby="button-addon1"
            />
          </label>
        </div>
          <label>
            <b-form-select class="filter" v-model="filter.status">
              <b-form-select-option
                v-for="option in filteredStatusSelectOptions"
                :value="option.value"
                :key="option.text"
              >
                {{ option.text }}
              </b-form-select-option>
            </b-form-select>
          </label>
          <template v-slot:end>
            <BaseButton v-stream:click="exportData$" class="app--mobile-hidden">匯出</BaseButton>
          </template>
      </BaseFilterToolbar>
      <DataTable
        v-show="list"
        :fields="fields"
        :mobileFields="mobileFields"
        :items="list"
        :total-result="total"
        :options.sync="options"
        :loading="loading"
        selectable
        @row-clicked="goToItem"
        @row-selected="onRowSelected"
        v-stream:export-click="exportData$"
        mobile-searchable
        mobile-exportable
        showRowDetails
      >
        <template v-slot:cell(status)="{ value }">
          <StatusLabel :item="value"></StatusLabel>
        </template>
        <template v-slot:cell(contactEmail)="{ value }">
          <div class="contact-email">{{ value }}</div>
        </template>

        <!-- mobile only -->
        <template v-slot:isShown="{ item }">
          <div class="contact-email">{{ item.isShown == "顯示" ? "顯示" : "不顯示" }}</div>
        </template>
        <!-- end of mobile only -->
      </DataTable>
      <b-skeleton-table v-if="!list" :rows="15" :columns="5"></b-skeleton-table>
    </section>
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex';
import { scheduled, asyncScheduler } from 'rxjs';
import axios from 'axios';
import {
  tap, switchMap, debounceTime, pluck, mergeAll,
} from 'rxjs/operators';
import {
  BFormSelect, BFormSelectOption, BContainer, BSkeletonTable,
} from 'bootstrap-vue';
import BaseButton from '@/components/BaseButton.vue';
import dataTableMixin from '../../mixins/data-table';
import exportMixin from '../../mixins/export';
import filtersQueryStringMixin from '../../mixins/filtersQueryString';
import optionsQueryStringMixin from '../../mixins/optionsQueryString';
import DataTable from '../../components/DataTable.vue';
import OverviewList from '../../components/overview/OverviewList.vue';
import StatusLabel from '../../components/StatusLabel.vue';
import TheTitle from '../../components/TheTitle.vue';
import TheListingTitle from '../../components/TheListingTitle.vue';
import BaseFilterToolbar from '../../components/BaseFilterToolbar.vue';

export default {
  mixins: [dataTableMixin, exportMixin, filtersQueryStringMixin, optionsQueryStringMixin],
  components: {
    StatusLabel,
    DataTable,
    TheTitle,
    TheListingTitle,
    BFormSelect,
    BFormSelectOption,
    BContainer,
    BSkeletonTable,
    OverviewList,
    BaseFilterToolbar,
    BaseButton,
  },
  data: () => ({
    fields: [
      { key: 'id', label: '營銷人員ID', sortable: true },
      { key: 'username', label: '營銷人員名稱', sortable: true },
      { key: 'email', label: '營銷人員電郵', sortable: true },
      { key: 'merchants', label: '商戶', sortable: false },
    ],
    mobileFields: [
      { key: 'id', label: '營銷人員ID', sortable: true },
      { key: 'username', label: '營銷人員名稱', sortable: true },
      { key: 'email', label: '營銷人員電郵', sortable: true },
    ],
    filter: {
      status: undefined,
      keywordType: 'all',
    },
    options: {
      page: 1,
      size: 15,
      order: 'desc',
    },
    keyword: '',
    keywordSelectOptions: [
      { value: 'all', text: '所有關鍵字' },
      { value: 'username', text: '營銷人員名稱' },
      { value: 'email', text: '營銷人員電郵' },
      { value: 'merchantName', text: '商戶名稱' },
    ],
    statusSelectOptions: [
      { value: undefined, text: '所有狀態' },
      { value: 'ACTIVATED', text: '已啓用' },
      { value: 'INACTIVATED', text: '已停用' },
    ],
    selected: [],
  }),
  computed: {
    ...mapGetters(['role']),
    overviews() {
      return this.result?.overviews;
    },
    list() {
      return this.result?.list.result;
    },
  },
  domStreams: ['onOverviewSelect$', 'exportData$'],
  subscriptions() {
    const getData$ = () => {
      const data = {
        ...this.filter,
        q: this.keyword,
      };
      // only admin can access merchant list
      return this.httpRequest$(
        {
          method: 'GET',
          url: `/agc-${this.role}-api/sales`,
        },
        data,
      );
    };

    return {
      exportData: this.exportData$.pipe(
        switchMap(() => axios({
          method: 'GET',
          url: `/agc-${this.role}-api/export/saleList`,
          params: {
            ...this.filter,
            q: this.keyword,
            selectedItem: this.selected.map((item) => item.id).join(','),
          },
          responseType: 'blob',
        }).then((res) => {
          this.downloadFile(res.data, this.generateFileName('sale-list-data'));
        })),
      ),
      result: scheduled(
        this.scheduleTasks, asyncScheduler,
      ).pipe(
        mergeAll(),
        tap(() => {
          this.loading = true;
        }),
        debounceTime(100),
        switchMap(getData$),
      ),
      onOverviewSelect: this.onOverviewSelect$.pipe(
        pluck('event', 'msg'),
        tap((status) => {
          this.filter.status = this.filter.status === status ? undefined : status;
        }),
      ),
    };
  },
  methods: {
    goToItem({ id }) {
      this.$router.push({
        path: `/${this.role}/sales/${id}`,
      });
    },
    onRowSelected(items) {
      this.selected = items;
    },
    initialize() {
      this.filter.keywordType = 'all';
      this.keyword = '';
      this.filter.status = undefined;
      setTimeout(() => {
        this.loading = false;
      }, 550);
      // when user updates filter too fast, the loading state will not set back to false
      // this time must be bigger than debounceTime's time
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../assets/scss/data-table/list-table-content';

.contact-email {
  max-width: 12rem;
}
</style>
